import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import Navbar from '../components/Navbar';
import SEO from '../components/common/SEO';
import api from '../lib/api';
import { Users, Award, Star, Music, Drama, Mic } from 'lucide-react';

const Team = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const res = await api.get('/teachers');
      setTeam(res.data || []);
    } catch (error) {
      console.error('Failed to fetch team:', error);
      // Fallback team data
      setTeam([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <SEO page="team" />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF007F]/10 to-transparent" />
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Meet the <span className="text-[#FF007F]">Team</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto">
              Our talented team of teachers and staff are dedicated to nurturing young performers. 
              With backgrounds spanning West End productions, professional dance companies, and prestigious training institutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4 border-y border-white/10 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#FF007F] mb-2">10+</div>
              <div className="text-white/60 text-sm">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#7000FF] mb-2">20+</div>
              <div className="text-white/60 text-sm">Expert Teachers</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#FF007F] mb-2">500+</div>
              <div className="text-white/60 text-sm">Students Trained</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#7000FF] mb-2">50+</div>
              <div className="text-white/60 text-sm">West End Alumni</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
            Our Faculty
          </h2>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-80 bg-white/5 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : team.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {team.map((member, index) => (
                <motion.div
                  key={member.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-white/5 border-white/10 hover:border-[#FF007F]/50 transition-colors overflow-hidden">
                    <div className="aspect-square bg-gradient-to-br from-[#FF007F]/20 to-[#7000FF]/20 flex items-center justify-center">
                      {member.photo_url ? (
                        <img 
                          src={member.photo_url} 
                          alt={`${member.first_name} ${member.last_name}`}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center">
                          <span className="text-4xl font-bold text-white/50">
                            {member.first_name?.[0]}{member.last_name?.[0]}
                          </span>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="text-lg font-bold text-white">
                        {member.first_name} {member.last_name}
                      </h3>
                      <p className="text-[#FF007F] text-sm">{member.specialization || 'Teacher'}</p>
                      {member.bio && (
                        <p className="text-white/60 text-sm mt-2 line-clamp-3">{member.bio}</p>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/60">Team information coming soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 px-4 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
            What We Believe In
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-[#0a0a0a] border-white/10 p-6">
              <Award className="w-10 h-10 text-[#FF007F] mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Excellence</h3>
              <p className="text-white/60 text-sm">
                We strive for excellence in everything we do, pushing our students to reach their full potential.
              </p>
            </Card>
            <Card className="bg-[#0a0a0a] border-white/10 p-6">
              <Star className="w-10 h-10 text-[#7000FF] mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Inclusivity</h3>
              <p className="text-white/60 text-sm">
                Every student is welcome, regardless of ability. We celebrate diversity and individual growth.
              </p>
            </Card>
            <Card className="bg-[#0a0a0a] border-white/10 p-6">
              <Music className="w-10 h-10 text-[#FF007F] mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Passion</h3>
              <p className="text-white/60 text-sm">
                We're passionate about performing arts and dedicated to inspiring the next generation.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 px-4 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
          Join Our Community
        </h2>
        <p className="text-white/60 mb-6 max-w-2xl mx-auto">
          Ready to start your performing arts journey? Book a free trial and meet our amazing team in person.
        </p>
        <a 
          href="/free-trial" 
          className="inline-flex items-center px-8 py-3 bg-[#FF007F] text-white font-bold rounded-lg hover:bg-[#D6006A] transition-colors"
        >
          Book Free Trial
        </a>
      </section>
    </div>
  );
};

export default Team;
